// Central registry of every UI language the dashboard offers.
//
// Coverage rationale: German + English as the base, every country bordering
// Austria (DE, CZ, SK, HU, SI, IT, CH/LI -> fr), the countries our caregivers
// are recruited from (RO, PL, HR, RS, BG) plus Russian and Ukrainian.
//
// `antdLocale` is the matching antd locale bundle (date pickers, pagination,
// table empty states, ...) so switching the language also switches the widget
// chrome, not just our own strings.
import type { Locale } from 'antd/es/locale';

import deDE from 'antd/locale/de_DE';
import enUS from 'antd/locale/en_US';
import roRO from 'antd/locale/ro_RO';
import plPL from 'antd/locale/pl_PL';
import skSK from 'antd/locale/sk_SK';
import csCZ from 'antd/locale/cs_CZ';
import huHU from 'antd/locale/hu_HU';
import slSI from 'antd/locale/sl_SI';
import hrHR from 'antd/locale/hr_HR';
import srRS from 'antd/locale/sr_RS';
import bgBG from 'antd/locale/bg_BG';
import itIT from 'antd/locale/it_IT';
import frFR from 'antd/locale/fr_FR';
import ruRU from 'antd/locale/ru_RU';
import ukUA from 'antd/locale/uk_UA';

export const LANGUAGE_CODES = [
  'de',
  'en',
  'ro',
  'pl',
  'sk',
  'cs',
  'hu',
  'sl',
  'hr',
  'sr',
  'bg',
  'it',
  'fr',
  'ru',
  'uk',
] as const;

export type Language = (typeof LANGUAGE_CODES)[number];

export interface LanguageDefinition {
  code: Language;
  /** Name in the language itself — that is what people look for in the list. */
  nativeName: string;
  /** German name, shown as a subtitle so admins recognise the entry. */
  germanName: string;
  /** Two-letter code rendered in the header button. */
  short: string;
  flag: string;
  antdLocale: Locale;
}

export const LANGUAGES: LanguageDefinition[] = [
  { code: 'de', nativeName: 'Deutsch',      germanName: 'Deutsch',        short: 'DE', flag: '🇩🇪', antdLocale: deDE },
  { code: 'en', nativeName: 'English',      germanName: 'Englisch',       short: 'EN', flag: '🇬🇧', antdLocale: enUS },
  { code: 'ro', nativeName: 'Română',       germanName: 'Rumänisch',      short: 'RO', flag: '🇷🇴', antdLocale: roRO },
  { code: 'pl', nativeName: 'Polski',       germanName: 'Polnisch',       short: 'PL', flag: '🇵🇱', antdLocale: plPL },
  { code: 'sk', nativeName: 'Slovenčina',   germanName: 'Slowakisch',     short: 'SK', flag: '🇸🇰', antdLocale: skSK },
  { code: 'cs', nativeName: 'Čeština',      germanName: 'Tschechisch',    short: 'CS', flag: '🇨🇿', antdLocale: csCZ },
  { code: 'hu', nativeName: 'Magyar',       germanName: 'Ungarisch',      short: 'HU', flag: '🇭🇺', antdLocale: huHU },
  { code: 'sl', nativeName: 'Slovenščina',  germanName: 'Slowenisch',     short: 'SL', flag: '🇸🇮', antdLocale: slSI },
  { code: 'hr', nativeName: 'Hrvatski',     germanName: 'Kroatisch',      short: 'HR', flag: '🇭🇷', antdLocale: hrHR },
  { code: 'sr', nativeName: 'Srpski',       germanName: 'Serbisch',       short: 'SR', flag: '🇷🇸', antdLocale: srRS },
  { code: 'bg', nativeName: 'Български',    germanName: 'Bulgarisch',     short: 'BG', flag: '🇧🇬', antdLocale: bgBG },
  { code: 'it', nativeName: 'Italiano',     germanName: 'Italienisch',    short: 'IT', flag: '🇮🇹', antdLocale: itIT },
  { code: 'fr', nativeName: 'Français',     germanName: 'Französisch',    short: 'FR', flag: '🇫🇷', antdLocale: frFR },
  { code: 'ru', nativeName: 'Русский',      germanName: 'Russisch',       short: 'RU', flag: '🇷🇺', antdLocale: ruRU },
  { code: 'uk', nativeName: 'Українська',   germanName: 'Ukrainisch',     short: 'UK', flag: '🇺🇦', antdLocale: ukUA },
];

export const LANGUAGE_BY_CODE: Record<Language, LanguageDefinition> = LANGUAGES.reduce(
  (acc, def) => {
    acc[def.code] = def;
    return acc;
  },
  {} as Record<Language, LanguageDefinition>,
);

export const DEFAULT_LANGUAGE: Language = 'de';

export const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && (LANGUAGE_CODES as readonly string[]).includes(value);
